import { message } from 'antd';
import { MapData_Base, MapData_ModelUtil, MapData_ProcessStatus } from '~/common/typescript/models/MapData';
import { remote_mapDataFile_getInfo, remote_mapDataFile_getContent } from '~/common/typescript/apis/MapDataFileAPIs';
import { remote_mapData_getOne } from '~/common/typescript/apis/MapDataAPIs';
import MapUtils from '~/components/Map/utils/MapUtils';

import { parseBoundingBox, getPreferredBoundingBox } from '~/components/Map/utils/BoundingBoxUtils';
export { parseBoundingBox, getPreferredBoundingBox };

/**
 * Phóng map tới Bounding Box [minLng, minLat, maxLng, maxLat] với highlight feature collection
 */
export const zoomToBBox = (bbox: [number, number, number, number], defaultZoom?: number) => {
  // Điểm đơn hoặc bbox quá nhỏ
  if (Math.abs(bbox[0] - bbox[2]) < 1e-7 && Math.abs(bbox[1] - bbox[3]) < 1e-7) {
    window.map?.flyTo({
      center: [bbox[0], bbox[1]],
      zoom: defaultZoom || 15,
      essential: true,
    });
    return;
  }

  const featureCollection = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [bbox[0], bbox[1]],
              [bbox[2], bbox[1]],
              [bbox[2], bbox[3]],
              [bbox[0], bbox[3]],
              [bbox[0], bbox[1]],
            ],
          ],
        },
      },
    ],
  };

  MapUtils.zoomToFeatureCollection(featureCollection as any);
};

export const LayerItemUtil = {
  zoomToLayer: async (layer: MapData_Base) => {
    // 1. Kiểm tra trạng thái xử lý
    if (
      layer.processStatus === MapData_ProcessStatus.SUBMITED ||
      layer.processStatus === MapData_ProcessStatus.PROCESSING ||
      layer.processStatus === MapData_ProcessStatus.PENDING
    ) {
      message.info(
        `Dữ liệu "${layer.name}" đang được xử lý tạo lát gạch (${MapData_ModelUtil.getTextStatus(
          layer.processStatus,
        )}). Vui lòng đợi trong giây lát.`,
      );
      return;
    }

    if (layer.processStatus === MapData_ProcessStatus.FAILED) {
      message.error(`Dữ liệu "${layer.name}" xử lý thất bại: ${layer.processMessage || 'Lỗi không xác định'}`);
      return;
    }

    let targetLayer = layer;
    // 2. Nếu thiếu metadata và file tile, thử fetch dữ liệu mới nhất từ server
    if (
      (!targetLayer.metadata || targetLayer.metadata === '{}') &&
      (!targetLayer.map_data_files || targetLayer.map_data_files.length === 0) &&
      targetLayer.id
    ) {
      try {
        console.log('LayerItemUtil - Metadata trống trên client, đang fetch dữ liệu mới nhất từ server...');
        const fresh = await remote_mapData_getOne(targetLayer.id);
        if (fresh) {
          targetLayer = fresh;
        }
      } catch (e) {
        console.warn('zoomToLayer: could not fetch fresh layer', e);
      }
    }

    let metadata: any = {};
    try {
      metadata = typeof targetLayer.metadata === 'string' ? JSON.parse(targetLayer.metadata || '{}') : (targetLayer.metadata || {});
    } catch {
      metadata = {};
    }

    // 0. ƯU TIÊN CAO NHẤT: iqr_bbox (Loại bỏ nhiễu/outlier từ các bản vẽ CAD/DGN)
    const iqrBBox = parseBoundingBox(metadata.iqr_bbox);
    if (iqrBBox) {
      console.log('LayerItemUtil - Phóng tới layer bằng iqr_bbox (ưu tiên chống outlier CAD):', iqrBBox);
      zoomToBBox(iqrBBox, metadata.default_zoom);
      return;
    }

    // 1. Ưu tiên kế tiếp: bbox trong metadata
    const standardBBox = parseBoundingBox(metadata.bbox);
    if (standardBBox) {
      console.log('LayerItemUtil - Phóng tới layer bằng metadata.bbox:', standardBBox);
      zoomToBBox(standardBBox, metadata.default_zoom);
      return;
    }

    // 2. Kiểm tra direct boundingBox trên layer hoặc metadata.bounds / metadata.boundingBox
    const directBoundsStr = (targetLayer as any).boundingBox || metadata.bounds || metadata.boundingBox;
    const directBBox = parseBoundingBox(directBoundsStr);
    if (directBBox) {
      console.log('LayerItemUtil - Phóng tới layer qua direct boundingBox:', directBBox);
      zoomToBBox(directBBox, metadata.default_zoom);
      return;
    }

    // 3. Fallback: Nếu metadata trên record bị thiếu, kiểm tra file metadata.json hoặc metadata.geojson đính kèm
    const metaFile = targetLayer.map_data_files?.find(
      (f: any) => f.name === 'metadata.json' || f.name === 'metadata.geojson' || f.type === 'metadata',
    );
    if (metaFile && metaFile.id) {
      try {
        console.log('LayerItemUtil - Đang tải nội dung file metadata đính kèm:', metaFile.name);
        const downloadedMeta: any = await remote_mapDataFile_getContent(metaFile.name, metaFile.id);
        const fileIqrBbox = parseBoundingBox(downloadedMeta?.iqr_bbox);
        if (fileIqrBbox) {
          console.log('LayerItemUtil - Phóng tới layer bằng iqr_bbox từ file metadata:', fileIqrBbox);
          zoomToBBox(fileIqrBbox, downloadedMeta?.default_zoom);
          return;
        }
        const fileBbox = parseBoundingBox(downloadedMeta?.bbox);
        if (fileBbox) {
          console.log('LayerItemUtil - Phóng tới layer bằng bbox từ file metadata:', fileBbox);
          zoomToBBox(fileBbox, downloadedMeta?.default_zoom);
          return;
        }
      } catch (err) {
        console.warn('LayerItemUtil - Không thể tải file metadata đính kèm:', err);
      }
    }

    // Xử lý cả gdalInfo/gdalinfo và ogrInfo/ogrinfo
    // Lưu ý: ogrInfo có thể là raw text (không phải JSON) với định dạng DGNV8
    let gdalInfo: any = null;
    let ogrInfo: any = null;

    const gdalInfoRaw = metadata.gdalInfo || metadata.gdalinfo;
    if (gdalInfoRaw && typeof gdalInfoRaw === 'string' && gdalInfoRaw.trim() !== '') {
      try {
        gdalInfo = JSON.parse(gdalInfoRaw);
      } catch (e) {
        console.log('gdalInfo không phải JSON hợp lệ, có thể là raw text:', gdalInfoRaw.substring(0, 100));
        gdalInfo = null;
      }
    } else if (gdalInfoRaw && typeof gdalInfoRaw === 'object') {
      gdalInfo = gdalInfoRaw;
    }

    const ogrInfoRaw = metadata.ogrInfo || metadata.ogrinfo;
    if (ogrInfoRaw && typeof ogrInfoRaw === 'string' && ogrInfoRaw.trim() !== '') {
      try {
        ogrInfo = JSON.parse(ogrInfoRaw);
      } catch (e) {
        console.log('ogrInfo không phải JSON hợp lệ (raw text từ GDAL), thử fallback sang tile archive:', ogrInfoRaw.substring(0, 100));
        ogrInfo = null;
      }
    } else if (ogrInfoRaw && typeof ogrInfoRaw === 'object') {
      ogrInfo = ogrInfoRaw;
    }

    // Chỉ xử lý nếu ogrInfo/gdalInfo là JSON hợp lệ (không null)
    if (ogrInfo) {
      console.log('ogrInfo:', ogrInfo);
      let bbox: number[] | null = null;
      
      // Thử extent trước [minX, minY, maxX, maxY]
      try {
        bbox = ogrInfo.layers?.[0]?.geometryFields?.[0]?.extent;
      } catch (error) {
        console.log('Error when parsing extent:', error);
      }
      
      // Fallback: envelope [minX, maxX, minY, maxY, minZ, maxZ]
      if (!bbox) {
        try {
          const envelope = ogrInfo.layers?.[0]?.geometryFields?.[0]?.envelope;
          if (envelope && envelope.length === 6) {
            bbox = [envelope[0], envelope[2], envelope[1], envelope[3]];
          }
        } catch (error) {
          console.log('Error when parsing envelope:', error);
        }
      }
      
      if (!bbox) {
        message.warning('Không tìm thấy bounding box cho layer này');
        return;
      }

      const featureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [bbox[0], bbox[1]],
                  [bbox[2], bbox[1]],
                  [bbox[2], bbox[3]],
                  [bbox[0], bbox[3]],
                  [bbox[0], bbox[1]],
                ],
              ],
            },
          },
        ],
      };
      MapUtils.zoomToFeatureCollection(featureCollection as any);
    } else if (gdalInfo) {
      console.log('gdalInfo:', gdalInfo);
      
      // Thử wgs84Extent trước
      if (gdalInfo.wgs84Extent) {
        const featureCollection = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: gdalInfo.wgs84Extent,
            },
          ],
        };
        MapUtils.zoomToFeatureCollection(featureCollection as any);
        return;
      }
      
      // Fallback: bounds từ metadata
      const gdalMetadata = gdalInfo?.metadata || {};
      const zoomMetadata = gdalMetadata[''] || {};
      const boundsStr = zoomMetadata.bounds;
      
      if (boundsStr) {
        const bbox = boundsStr.split(',').map(Number);
        const featureCollection = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Polygon',
                coordinates: [
                  [
                    [bbox[0], bbox[1]],
                    [bbox[2], bbox[1]],
                    [bbox[2], bbox[3]],
                    [bbox[0], bbox[3]],
                    [bbox[0], bbox[1]],
                  ],
                ],
              },
            },
          ],
        };
        MapUtils.zoomToFeatureCollection(featureCollection as any);
      } else {
        message.warning('Không tìm thấy bounding box cho layer raster này');
      }
    } else {
      // Không có ogrInfo/gdalInfo trong metadata - thử lấy từ PMTiles/MBTiles/GeoJSON file
      console.log('Không có ogrInfo/gdalInfo trong metadata, thử lấy từ tile file hoặc geojson');
      await LayerItemUtil.zoomToLayerFromTileFile(layer);
    }
  },

  /**
   * Zoom đến layer bằng cách đọc metadata từ PMTiles / MBTiles file hoặc GeoJSON fallback
   * Áp dụng cho các định dạng không có ogrInfo/gdalInfo trong metadata (DGNV8, modern pipelines)
   */
  zoomToLayerFromTileFile: async (layer: MapData_Base) => {
    try {
      let targetLayer = layer;
      if ((!targetLayer.map_data_files || targetLayer.map_data_files.length === 0) && targetLayer.id) {
        try {
          const fresh = await remote_mapData_getOne(targetLayer.id);
          if (fresh) targetLayer = fresh;
        } catch (e) {
          console.warn('zoomToLayerFromTileFile: could not fetch fresh layer', e);
        }
      }

      // 0. Kiểm tra iqr_bbox hoặc bbox trong metadata nếu có
      let metadata: any = {};
      try {
        metadata = typeof targetLayer.metadata === 'string' ? JSON.parse(targetLayer.metadata || '{}') : (targetLayer.metadata || {});
      } catch {}

      const iqrBBox = parseBoundingBox(metadata.iqr_bbox);
      if (iqrBBox) {
        console.log('zoomToLayerFromTileFile - Sử dụng iqr_bbox từ metadata:', iqrBBox);
        zoomToBBox(iqrBBox, metadata.default_zoom);
        return;
      }

      const standardBBox = parseBoundingBox(metadata.bbox);
      if (standardBBox) {
        console.log('zoomToLayerFromTileFile - Sử dụng bbox từ metadata:', standardBBox);
        zoomToBBox(standardBBox, metadata.default_zoom);
        return;
      }

      // 1. Tìm file pmtiles hoặc mbtiles trong map_data_files
      const tileFile = targetLayer.map_data_files?.find((file: any) => file.type === 'pmtiles' || file.type === 'mbtiles');

      if (tileFile) {
        const filePath = tileFile.path;
        console.log('Đang lấy metadata từ file tile (PMTiles/MBTiles):', filePath);

        // Gọi API lấy thông tin tile archive
        const tileInfo = await remote_mapDataFile_getInfo(filePath);

        if (tileInfo && tileInfo.bounds && Array.isArray(tileInfo.bounds) && tileInfo.bounds.length >= 4) {
          console.log('Tile archive info:', tileInfo);
          const bbox = tileInfo.bounds;

          // Nếu bounds bị kẹp toàn cầu [-180, ..., 180, ...] mà có center thì zoom tới center
          if (
            Math.abs(bbox[0]) >= 179.9 &&
            Math.abs(bbox[2]) >= 179.9 &&
            tileInfo.center &&
            Array.isArray(tileInfo.center) &&
            tileInfo.center.length >= 2
          ) {
            const center = tileInfo.center;
            const zoom = center[2] || 16;
            window.map?.flyTo({
              center: [center[0], center[1]],
              zoom: zoom,
              essential: true,
            });
            return;
          }

          const featureCollection = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Polygon',
                  coordinates: [
                    [
                      [bbox[0], bbox[1]],
                      [bbox[2], bbox[1]],
                      [bbox[2], bbox[3]],
                      [bbox[0], bbox[3]],
                      [bbox[0], bbox[1]],
                    ],
                  ],
                },
              },
            ],
          };

          MapUtils.zoomToFeatureCollection(featureCollection as any);
          return;
        }
      }

      // 2. Fallback: Nếu có file GeoJSON
      const geojsonFile = layer.map_data_files?.find((file: any) => file.type === 'geojson');
      if (geojsonFile && geojsonFile.id) {
        console.log('Thử zoom qua file GeoJSON:', geojsonFile);
        try {
          const content = await remote_mapDataFile_getContent(geojsonFile.name, geojsonFile.id);
          if (content && (content as any).type === 'FeatureCollection' && (content as any).features?.length > 0) {
            MapUtils.zoomToFeatureCollection(content as any);
            return;
          }
        } catch (e) {
          console.log('Không thể đọc GeoJSON để zoom:', e);
        }
      }

      message.warning('Không tìm thấy thông tin bounding box để phóng tới layer này');
    } catch (error) {
      console.error('Lỗi khi lấy thông tin phóng tới:', error);
      message.warning('Không thể lấy thông tin phóng tới cho layer này');
    }
  },

  // Giữ alias cho backward compatibility
  zoomToLayerFromMbtiles: async (layer: MapData_Base) => {
    return LayerItemUtil.zoomToLayerFromTileFile(layer);
  },
};
